import {
  AlertTriangle,
  Bug,
  Check,
  CheckCircle2,
  Clock,
  Edit3,
  ExternalLink,
  FileText,
  Headphones,
  History,
  Loader2,
  Mail,
  MessageSquare,
  MinusCircle,
  MoreHorizontal,
  Pause,
  Phone,
  PhoneCall,
  Play,
  RefreshCw,
  Send,
  SkipForward,
  UserCog,
  X,
} from 'lucide-react';
import React, { useState } from 'react';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useGetTaskDeliverablesQuery } from '@/services/api/deliverablesService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import {
  DeliverableMatchStatus,
  DeliverableStatus,
} from '@/services/types/deliverables-types';

import { EmailPreviewModal } from './EmailPreviewModal';

// Animated styles
const animatedStyles = `
  @keyframes dash-move {
    0% { background-position: 0 0; }
    100% { background-position: 12px 0; }
  }
  @keyframes shimmer {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  @keyframes pulse-ring {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.5); opacity: 0; }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface SLAStep {
  step_id: string;
  label: string;
  delay_days: number;
  step_type: string;
  requires_manual_task: boolean;
  escalation_level: number;
}

export interface Campaign {
  id: string;
  matter_id: string;
  coverage_id: string;
  workflow_id: string;
  email_thread_id?: string;
  status: string;
  current_step_index: number;
  current_step_label: string;
  next_action_date: string | null;
  next_action_type: string | null;
  next_action_label: string;
  last_contact_date: string | null;
  total_contact_count: number;
  created_at: string;
}

export interface Activity {
  id: string;
  step_id: string;
  step_label: string;
  activity_type: string;
  executed_at: string;
  scheduled_at: string | null;
  was_manual: boolean;
  notes: string;
}

// Human-readable labels for activity types
const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  auto_email: 'Email Sent',
  auto_fax: 'Fax Sent',
  manual_call: 'Phone Call',
  manual_email: 'Email Sent',
  supervisor_escalation: 'Supervisor Escalation',
};

const getActivityTypeLabel = (activityType: string): string => {
  return ACTIVITY_TYPE_LABELS[activityType] || activityType;
};

// Get an icon for the activity type
const getActivityTypeIcon = (activityType: string): React.ReactNode => {
  switch (activityType) {
    case 'auto_email':
    case 'manual_email':
      return <Mail className="h-3 w-3" />;
    case 'auto_fax':
      return <Send className="h-3 w-3" />;
    case 'manual_call':
      return <PhoneCall className="h-3 w-3" />;
    case 'supervisor_escalation':
      return <UserCog className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
};

export interface LOAChaserProgressProps {
  campaign: Campaign;
  activities?: Activity[]; // Now optional - fetched from domain events
  slaSteps: SLAStep[];
  onSendSignal: (
    signal: string,
    params: Record<string, string | number>,
  ) => Promise<void>;
  onOpenEmail?: () => void;
  loading?: boolean;
  /** Carrier name for call modal context */
  carrierName?: string;
  /** Matter ID for call modal */
  matterId?: string;
  /** Task ID for displaying deliverables */
  taskId?: string;
  /** Callback when email is sent via preview modal - triggers campaign refresh */
  onEmailSent?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

const getStepIcon = (stepType: string) => {
  switch (stepType) {
    case 'auto_email':
    case 'manual_email':
      return <Mail className="h-3 w-3" />;
    case 'manual_call':
      return <Phone className="h-3 w-3" />;
    case 'supervisor_escalation':
      return <UserCog className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'running':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'completed_loa_received':
      return 'Complete';
    case 'stopped_manually':
      return 'Stopped';
    case 'monthly_cadence':
      return 'Monthly';
    default:
      return status.replace(/_/g, ' ');
  }
};

const formatRelativeTime = (dateStr: string | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) return 'overdue';
  if (diffHours <= 1) return 'in ~1 hour';
  if (diffHours < 24) return `in ~${diffHours} hours`;
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays} days`;
};

const formatShortDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatFullDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ============================================================================
// Step Pill with Popover
// ============================================================================

interface StepPillProps {
  step: SLAStep;
  index: number;
  isActive: boolean;
  isPast: boolean;
  isFuture: boolean;
  isCampaignPaused: boolean;
  canInteract: boolean;
  onAction: (signal: string, params: Record<string, string | number>) => void;
  onMakeCall?: () => void;
  onOpenEmailPreview?: (stepId?: string) => void;
  loading?: boolean;
}

const StepPill: React.FC<StepPillProps> = ({
  step,
  index,
  isActive,
  isPast,
  isFuture,
  isCampaignPaused,
  canInteract,
  onAction,
  onMakeCall,
  onOpenEmailPreview,
  loading,
}) => {
  const shortLabel = step.label.replace(/Day \d+:\s*/, '');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'relative flex flex-col items-center gap-1 px-2 py-1.5 text-[11px] font-medium transition-colors rounded hover:bg-zinc-100 cursor-pointer min-w-[56px]',
            isActive && !isCampaignPaused && 'text-violet-700',
            isActive && isCampaignPaused && 'text-amber-600',
            isPast && 'text-zinc-600',
            isFuture && 'text-zinc-400',
          )}
        >
          {isPast ? (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check className="h-3.5 w-3.5" />
            </span>
          ) : isActive ? (
            <span className="relative">
              {/* Pulsing ring when loading */}
              {loading && (
                <span
                  className={cn(
                    'absolute inset-0 rounded-full',
                    isCampaignPaused ? 'bg-amber-400' : 'bg-violet-400',
                  )}
                  style={{ animation: 'pulse-ring 1.5s ease-out infinite' }}
                />
              )}
              <span
                className={cn(
                  'relative flex h-6 w-6 items-center justify-center rounded-full text-white ring-4',
                  isCampaignPaused
                    ? 'bg-amber-500 ring-amber-100'
                    : 'bg-violet-600 ring-violet-100',
                )}
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : isCampaignPaused ? (
                  <Pause className="h-3 w-3" />
                ) : (
                  getStepIcon(step.step_type)
                )}
              </span>
            </span>
          ) : (
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full border-2',
                isCampaignPaused
                  ? 'border-zinc-200 text-zinc-400'
                  : 'border-zinc-300 text-zinc-500',
              )}
            >
              <span className="text-[10px] font-semibold">{index + 1}</span>
            </span>
          )}
          <span className="text-center leading-tight max-w-[64px]">
            {shortLabel}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-52 p-0 shadow-none border border-zinc-200"
        align="center"
        sideOffset={6}
      >
        <div className="px-3 py-2 border-b border-zinc-100">
          <p className="text-xs font-semibold text-zinc-900">{step.label}</p>
          <p className="text-[11px] font-medium text-zinc-600">
            +{step.delay_days}d · {step.step_type.replace(/_/g, ' ')}
          </p>
        </div>

        <div className="p-1.5 space-y-0.5">
          {/* Active auto step (email) - can edit and send */}
          {isActive && canInteract && !step.requires_manual_task && (
            <button
              type="button"
              onClick={() => onOpenEmailPreview?.(step.step_id)}
              disabled={loading}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
              ) : (
                <Edit3 className="h-3.5 w-3.5 text-violet-500" />
              )}
              <div className="text-left">
                <span>Edit &amp; Send</span>
                <p className="text-[10px] text-zinc-500 font-normal">
                  Preview and customize before sending
                </p>
              </div>
            </button>
          )}

          {/* Active manual step - show Make Call button for calls, instructions for others */}
          {isActive &&
            canInteract &&
            step.requires_manual_task &&
            (step.step_type === 'manual_call' && onMakeCall ? (
              <button
                type="button"
                onClick={() => {
                  onMakeCall();
                }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors"
              >
                <PhoneCall className="h-3.5 w-3.5 text-violet-600" />
                <div className="text-left">
                  <span>Make Call</span>
                  <p className="text-[10px] text-zinc-500 font-normal">
                    Call carrier through RingCentral
                  </p>
                </div>
              </button>
            ) : (
              <div className="px-2.5 py-2 text-[11px] text-zinc-600">
                <p className="font-medium text-zinc-700 mb-1">
                  {step.step_type === 'supervisor_escalation'
                    ? 'Escalate to supervisor'
                    : 'Complete the required task'}
                </p>
                <p className="text-zinc-500">
                  Log completion using the button below
                </p>
              </div>
            ))}

          {/* Past or Future steps */}
          {(isPast || isFuture) && (
            <>
              {/* Set to step - available for all step types */}
              <button
                type="button"
                onClick={() => onAction('set_step', { step_id: step.step_id })}
                disabled={loading}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 text-zinc-500 animate-spin" />
                ) : (
                  <Clock className="h-3.5 w-3.5 text-zinc-500" />
                )}
                <div className="text-left">
                  <span>Set to this step</span>
                  <p className="text-[10px] text-zinc-500 font-normal">
                    {step.requires_manual_task
                      ? `Wait ${step.delay_days}d, then notify`
                      : `Wait ${step.delay_days}d, then send email`}
                  </p>
                </div>
              </button>

              {/* Edit & Send - only for auto steps (emails) */}
              {!step.requires_manual_task && (
                <button
                  type="button"
                  onClick={() => onOpenEmailPreview?.(step.step_id)}
                  disabled={loading}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
                  ) : (
                    <Edit3 className="h-3.5 w-3.5 text-violet-500" />
                  )}
                  <div className="text-left">
                    <span>Edit &amp; Send</span>
                    <p className="text-[10px] text-zinc-500 font-normal">
                      Preview and customize before sending
                    </p>
                  </div>
                </button>
              )}
            </>
          )}

          {isActive && !canInteract && (
            <p className="px-2.5 py-1.5 text-[11px] font-medium text-zinc-600 italic">
              Campaign not active
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// Activity History Modal
// ============================================================================

interface ActivityModalProps {
  open: boolean;
  onClose: () => void;
  activities: Activity[];
  campaign: Campaign;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  open,
  onClose,
  activities,
  campaign,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-zinc-100">
          <DialogTitle className="text-sm font-semibold">
            Activity History
          </DialogTitle>
          <p className="text-[11px] font-medium text-zinc-600 mt-0.5">
            {activities.length} activities · Started{' '}
            {formatShortDate(campaign.created_at)}
          </p>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto">
          {activities.length === 0 ? (
            <div className="py-8 text-center">
              <History className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
              <p className="text-xs font-medium text-zinc-600">
                No activities yet
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-50">
              {activities.map((activity, idx) => (
                <div
                  key={activity.id}
                  className={cn(
                    'px-4 py-2.5 flex gap-3',
                    idx === 0 && 'bg-zinc-50/50',
                  )}
                >
                  {/* Timeline icon */}
                  <div className="flex flex-col items-center pt-0.5">
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center',
                        activity.was_manual
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-violet-100 text-violet-600',
                      )}
                    >
                      {getActivityTypeIcon(activity.activity_type)}
                    </div>
                    {idx < activities.length - 1 && (
                      <div className="w-px flex-1 bg-zinc-200 mt-1" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-zinc-800">
                          {getActivityTypeLabel(activity.activity_type)}
                        </p>
                        {activity.step_label && (
                          <p className="text-[11px] font-medium text-zinc-500 truncate">
                            {activity.step_label}
                          </p>
                        )}
                      </div>
                      <span
                        className={cn(
                          'text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0',
                          activity.was_manual
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-violet-50 text-violet-700',
                        )}
                      >
                        {activity.was_manual ? 'Manual' : 'Auto'}
                      </span>
                    </div>
                    <p className="text-[11px] font-medium text-zinc-600 mt-0.5">
                      {formatFullDate(activity.executed_at)}
                    </p>
                    {activity.notes && (
                      <p className="text-[11px] font-medium text-zinc-700 mt-1 bg-zinc-50 px-2 py-1 rounded italic">
                        &quot;{activity.notes}&quot;
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Debug Modal
// ============================================================================

interface DebugModalProps {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
  slaSteps: SLAStep[];
}

const DebugModal: React.FC<DebugModalProps> = ({
  open,
  onClose,
  campaign,
  slaSteps,
}) => {
  const currentStep = slaSteps[campaign.current_step_index];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-zinc-100">
          <DialogTitle className="text-sm font-semibold flex items-center gap-2">
            <Bug className="h-4 w-4 text-zinc-600" />
            Debug Info
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 space-y-3 text-xs font-mono">
          <div>
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
              Workflow ID
            </p>
            <p className="text-zinc-800 break-all select-all bg-zinc-50 px-2 py-1 rounded">
              {campaign.workflow_id}
            </p>
          </div>

          {campaign.email_thread_id && (
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Email Thread ID
              </p>
              <p className="text-zinc-800 break-all select-all bg-zinc-50 px-2 py-1 rounded">
                {campaign.email_thread_id}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Campaign ID
              </p>
              <p className="text-zinc-800 truncate">{campaign.id}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Status
              </p>
              <p className="text-zinc-800">{campaign.status}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Step Index
              </p>
              <p className="text-zinc-800">{campaign.current_step_index}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Step Type
              </p>
              <p className="text-zinc-800">{currentStep?.step_type || '—'}</p>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
              Next Action
            </p>
            <p className="text-zinc-800">
              {campaign.next_action_date
                ? formatFullDate(campaign.next_action_date)
                : '—'}
            </p>
          </div>

          <div>
            <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
              Last Contact
            </p>
            <p className="text-zinc-800">
              {campaign.last_contact_date
                ? formatFullDate(campaign.last_contact_date)
                : '—'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Matter ID
              </p>
              <p className="text-zinc-800 truncate">{campaign.matter_id}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide mb-1">
                Coverage ID
              </p>
              <p className="text-zinc-800 truncate">{campaign.coverage_id}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Actions Menu
// ============================================================================

interface ActionsMenuProps {
  campaign: Campaign;
  onAction: (signal: string, params: Record<string, string | number>) => void;
  onOpenDebug: () => void;
  loading?: boolean;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  campaign,
  onAction,
  onOpenDebug,
  loading,
}) => {
  const [open, setOpen] = useState(false);
  const isRunning = campaign.status === 'running';
  const isComplete = campaign.status === 'completed_loa_received';

  const handleAction = (
    signal: string,
    params: Record<string, string | number>,
  ) => {
    setOpen(false);
    onAction(signal, params);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="p-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-44 p-1.5 shadow-none border border-zinc-200"
        align="end"
        sideOffset={4}
      >
        <button
          type="button"
          onClick={() => handleAction('loa_task_completed', {})}
          disabled={loading || isComplete}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Mark LOA Received
        </button>

        <div className="my-1 border-t border-zinc-100" />

        <button
          type="button"
          onClick={() => handleAction('snooze', { days: 7 })}
          disabled={loading || !isRunning}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Clock className="h-3.5 w-3.5" />
          )}
          Snooze 7 Days
        </button>

        <div className="my-1 border-t border-zinc-100" />

        <button
          type="button"
          onClick={() => {
            setOpen(false);
            onOpenDebug();
          }}
          className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50 rounded transition-colors"
        >
          <Bug className="h-3.5 w-3.5" />
          Debug
        </button>
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// Smart CTA
// ============================================================================

interface SmartCTAProps {
  currentStep: SLAStep | null;
  onAction: (signal: string, params: Record<string, string | number>) => void;
  onMakeCall?: () => void;
  onOpenEmailPreview?: (stepId?: string) => void;
  loading?: boolean;
}

const SmartCTA: React.FC<SmartCTAProps> = ({
  currentStep,
  onAction,
  onMakeCall,
  onOpenEmailPreview,
  loading,
}) => {
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  if (!currentStep) return null;

  if (currentStep.requires_manual_task) {
    const isEscalation = currentStep.step_type === 'supervisor_escalation';
    const isCall = currentStep.step_type === 'manual_call';

    let buttonLabel = 'Mark Done';
    let icon = <Check className="h-3.5 w-3.5" />;
    let activityType = 'manual_task';
    let placeholder = 'Notes (optional)...';

    if (isEscalation) {
      buttonLabel = 'Log Escalation';
      icon = <AlertTriangle className="h-3.5 w-3.5" />;
      activityType = 'supervisor_escalation';
      placeholder = 'Escalation outcome...';
    } else if (isCall) {
      buttonLabel = 'Log Call';
      icon = <Phone className="h-3.5 w-3.5" />;
      activityType = 'manual_call';
      placeholder = 'Call outcome...';
    }

    if (showNotes) {
      return (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={placeholder}
            className="w-32 px-2 py-1 text-[11px] font-medium border border-zinc-300 rounded focus:outline-none focus:border-violet-400"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onAction('manual_activity', {
                  activity_type: activityType,
                  notes,
                });
                setNotes('');
                setShowNotes(false);
              }
              if (e.key === 'Escape') {
                setShowNotes(false);
                setNotes('');
              }
            }}
          />
          <button
            type="button"
            onClick={() => {
              onAction('manual_activity', {
                activity_type: activityType,
                notes,
              });
              setNotes('');
              setShowNotes(false);
            }}
            disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded disabled:opacity-50"
          >
            {loading && <Loader2 className="h-3 w-3 animate-spin" />}
            Done
          </button>
          <button
            type="button"
            onClick={() => {
              setShowNotes(false);
              setNotes('');
            }}
            className="p-1 text-zinc-600 hover:text-zinc-800"
            disabled={loading}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      );
    }

    // For manual_call, show both Make Call and Log Call buttons
    if (isCall && onMakeCall) {
      return (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onMakeCall}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded disabled:opacity-50"
          >
            <PhoneCall className="h-3.5 w-3.5" />
            Make Call
          </button>
          <button
            type="button"
            onClick={() => setShowNotes(true)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-violet-700 border border-violet-300 hover:bg-violet-50 rounded disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
            {buttonLabel}
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => setShowNotes(true)}
        disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-violet-700 border border-violet-300 hover:bg-violet-50 rounded disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
        {buttonLabel}
      </button>
    );
  }

  // Auto step (email) - "Edit & Send" opens the preview modal
  return (
    <button
      type="button"
      onClick={() => onOpenEmailPreview?.(currentStep.step_id)}
      disabled={loading}
      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-violet-700 border border-violet-300 hover:bg-violet-50 rounded disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Edit3 className="h-3.5 w-3.5" />
      )}
      Edit &amp; Send
    </button>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const LOAChaserProgress: React.FC<LOAChaserProgressProps> = ({
  campaign,
  activities = [],
  slaSteps,
  onSendSignal,
  onOpenEmail,
  loading = false,
  carrierName,
  matterId,
  taskId,
  onEmailSent,
}) => {
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false);
  const [emailPreviewStepId, setEmailPreviewStepId] = useState<
    string | undefined
  >(undefined);

  // Fetch deliverables for the task
  const { data: deliverables } = useGetTaskDeliverablesQuery(
    { matterId: matterId || '', taskId: taskId || '' },
    { skip: !matterId || !taskId },
  );

  const currentStep = slaSteps[campaign.current_step_index] || null;
  const isRunning = campaign.status === 'running';
  const isPaused = campaign.status === 'paused';
  const isStopped = campaign.status === 'stopped_manually';
  const isComplete = campaign.status === 'completed_loa_received';
  const isMonthlyCadence = campaign.status === 'monthly_cadence';

  const canInteract = isRunning || isPaused || isMonthlyCadence;

  const handleAction = (
    signal: string,
    params: Record<string, string | number>,
  ) => {
    onSendSignal(signal, params);
  };

  const handleMakeCall = () => {
    setCallModalOpen(true);
  };

  const handleOpenEmailPreview = (stepId?: string) => {
    setEmailPreviewStepId(stepId);
    setEmailPreviewModalOpen(true);
  };

  const handleEmailSent = () => {
    onEmailSent?.();
  };

  return (
    <>
      <style>{animatedStyles}</style>
      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-1 bg-zinc-900">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white">LOA Chaser</span>
            <span
              className={cn(
                'px-1.5 py-0.5 text-[10px] font-semibold rounded',
                isRunning && 'bg-emerald-500/20 text-emerald-400',
                isPaused && 'bg-amber-500/20 text-amber-400',
                isComplete && 'bg-violet-500/20 text-violet-400',
                isStopped && 'bg-zinc-700 text-zinc-400',
                isMonthlyCadence && 'bg-sky-500/20 text-sky-400',
              )}
            >
              {getStatusLabel(campaign.status)}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Open Email shortcut */}
            {onOpenEmail && (
              <button
                type="button"
                onClick={onOpenEmail}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 rounded transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open Email
              </button>
            )}

            {/* Primary action buttons */}
            {isRunning && (
              <button
                type="button"
                onClick={() => handleAction('loa_detected', {})}
                disabled={loading}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 rounded transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Pause className="h-3.5 w-3.5" />
                )}
                Pause
              </button>
            )}
            {(isPaused || isStopped) && (
              <button
                type="button"
                onClick={() => handleAction('resume', {})}
                disabled={loading}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-violet-400 hover:bg-zinc-800 rounded transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                Resume
              </button>
            )}

            {/* Activity history */}
            <button
              type="button"
              onClick={() => setActivityModalOpen(true)}
              className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
            >
              <History className="h-3.5 w-3.5" />
              <span>{activities.length}</span>
            </button>

            {/* More actions */}
            <ActionsMenu
              campaign={campaign}
              onAction={handleAction}
              onOpenDebug={() => setDebugModalOpen(true)}
              loading={loading}
            />
          </div>
        </div>

        {/* Centered Progress Steps */}
        <div className="flex justify-center items-start gap-0 px-4 py-3 border-b border-zinc-100 overflow-x-auto">
          {/* Starting Point - LOR Sent */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex flex-col items-center gap-1 px-2 py-1.5 text-[11px] font-medium text-emerald-600 rounded hover:bg-zinc-100 cursor-pointer transition-colors min-w-[56px]"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <Send className="h-3 w-3" />
                </span>
                <span className="text-center leading-tight">
                  LOR
                  <br />
                  Sent
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-52 p-0 shadow-none border border-zinc-200"
              align="center"
              sideOffset={6}
            >
              <div className="px-3 py-2 border-b border-zinc-100">
                <p className="text-xs font-semibold text-zinc-900">LOR Sent</p>
                <p className="text-[11px] font-medium text-zinc-600">
                  Campaign starting point
                </p>
              </div>
              <div className="p-1.5">
                {slaSteps.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      handleAction('set_step', {
                        step_id: slaSteps[0].step_id,
                        execute: 1,
                      })
                    }
                    disabled={loading}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium text-zinc-800 hover:bg-zinc-50 rounded transition-colors disabled:opacity-50"
                  >
                    {loading ? (
                      <Loader2 className="h-3.5 w-3.5 text-zinc-500 animate-spin" />
                    ) : (
                      <SkipForward className="h-3.5 w-3.5 rotate-180 text-zinc-500" />
                    )}
                    <div className="text-left">
                      <span>Restart campaign</span>
                      <p className="text-[10px] text-zinc-500 font-normal">
                        Go back to step 1
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Line after LOR - animated when first step is active */}
          <div className="flex items-center self-center mt-3">
            {campaign.current_step_index === 0 && !isPaused ? (
              <div
                className="w-6 h-0.5 overflow-hidden"
                style={{
                  background: `repeating-linear-gradient(
                    90deg,
                    rgb(139 92 246) 0px,
                    rgb(139 92 246) 3px,
                    transparent 3px,
                    transparent 6px
                  )`,
                  backgroundSize: '200% 100%',
                  animation: 'dash-move 1s linear infinite',
                }}
              />
            ) : (
              <div
                className={cn(
                  'w-6 h-0.5',
                  campaign.current_step_index === 0 &&
                    isPaused &&
                    'bg-amber-300',
                  campaign.current_step_index > 0 && 'bg-emerald-400',
                )}
              />
            )}
          </div>

          {slaSteps.map((step, index) => {
            const isActive = index === campaign.current_step_index;
            const isPast = index < campaign.current_step_index;
            const isFuture = index > campaign.current_step_index;
            // Animated dashed line goes FROM previous step TO current active step
            const isLineToActive = isActive && index > 0;

            return (
              <React.Fragment key={step.step_id}>
                {index > 0 && (
                  <div className="flex items-center self-center mt-3">
                    {isLineToActive && !isPaused ? (
                      <div
                        className="w-6 h-0.5 overflow-hidden"
                        style={{
                          background: `repeating-linear-gradient(
                            90deg,
                            rgb(139 92 246) 0px,
                            rgb(139 92 246) 3px,
                            transparent 3px,
                            transparent 6px
                          )`,
                          backgroundSize: '200% 100%',
                          animation: 'dash-move 1s linear infinite',
                        }}
                      />
                    ) : (
                      <div
                        className={cn(
                          'w-6 h-0.5',
                          isPast && 'bg-emerald-400',
                          isActive && isPaused && 'bg-amber-300',
                          isActive && !isPaused && 'bg-violet-300',
                          isFuture && 'bg-zinc-200',
                        )}
                      />
                    )}
                  </div>
                )}
                <StepPill
                  step={step}
                  index={index}
                  isActive={isActive}
                  isPast={isPast}
                  isFuture={isFuture}
                  isCampaignPaused={isPaused}
                  canInteract={canInteract}
                  onAction={handleAction}
                  onMakeCall={handleMakeCall}
                  onOpenEmailPreview={handleOpenEmailPreview}
                  loading={loading}
                />
              </React.Fragment>
            );
          })}

          {/* Final state indicator - show for monthly cadence, completed, stopped, or when past all steps */}
          {/* Don't show when paused at last step - the last step pill already shows paused state */}
          {(campaign.status === 'monthly_cadence' ||
            campaign.current_step_index >= slaSteps.length ||
            isComplete ||
            isStopped) && (
            <>
              {/* Connector line */}
              <div className="flex items-center self-center mt-3">
                {isComplete ? (
                  <div className="w-6 h-0.5 bg-emerald-400" />
                ) : isStopped ? (
                  <div className="w-6 h-0.5 bg-zinc-300" />
                ) : isPaused ? (
                  <div className="w-6 h-0.5 bg-amber-300" />
                ) : (
                  <div
                    className="w-6 h-0.5 overflow-hidden"
                    style={{
                      background: `repeating-linear-gradient(
                        90deg,
                        rgb(14 165 233) 0px,
                        rgb(14 165 233) 3px,
                        transparent 3px,
                        transparent 6px
                      )`,
                      backgroundSize: '200% 100%',
                      animation: 'dash-move 2s linear infinite',
                    }}
                  />
                )}
              </div>
              {/* Final state pill */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'relative flex flex-col items-center gap-1 px-2 py-1.5 text-[11px] font-medium rounded hover:bg-zinc-100 cursor-pointer transition-colors min-w-[56px]',
                      isComplete && 'text-emerald-600',
                      isStopped && 'text-zinc-500',
                      isPaused && !isComplete && !isStopped && 'text-amber-600',
                      !isComplete && !isStopped && !isPaused && 'text-sky-600',
                    )}
                  >
                    <span className="relative">
                      {/* Pulse animation only for active monthly cadence */}
                      {!isPaused && !isComplete && !isStopped && (
                        <span
                          className="absolute inset-0 rounded-full bg-sky-400"
                          style={{
                            animation: 'pulse-ring 2s ease-out infinite',
                          }}
                        />
                      )}
                      <span
                        className={cn(
                          'relative flex h-6 w-6 items-center justify-center rounded-full text-white ring-4',
                          isComplete && 'bg-emerald-500 ring-emerald-100',
                          isStopped && 'bg-zinc-400 ring-zinc-100',
                          isPaused &&
                            !isComplete &&
                            !isStopped &&
                            'bg-amber-500 ring-amber-100',
                          !isComplete &&
                            !isStopped &&
                            !isPaused &&
                            'bg-sky-500 ring-sky-100',
                        )}
                      >
                        {loading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : isComplete ? (
                          <Check className="h-3 w-3" />
                        ) : isStopped ? (
                          <X className="h-3 w-3" />
                        ) : isPaused ? (
                          <Pause className="h-3 w-3" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                      </span>
                    </span>
                    <span className="text-center leading-tight">
                      {isComplete ? (
                        <>
                          LOA
                          <br />
                          Received
                        </>
                      ) : isStopped ? (
                        <>
                          Campaign
                          <br />
                          Stopped
                        </>
                      ) : (
                        <>
                          Every
                          <br />
                          30d
                        </>
                      )}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-56 p-0 shadow-none border border-zinc-200"
                  align="center"
                  sideOffset={6}
                >
                  <div className="px-3 py-2 border-b border-zinc-100">
                    <p className="text-xs font-semibold text-zinc-900">
                      {isComplete
                        ? 'LOA Received'
                        : isStopped
                          ? 'Campaign Stopped'
                          : 'Monthly Follow-Up'}
                    </p>
                    <p className="text-[11px] font-medium text-zinc-600">
                      {isComplete
                        ? 'Letter of Authorization has been received'
                        : isStopped
                          ? 'Campaign was manually stopped'
                          : isPaused
                            ? 'Paused - waiting for LOA confirmation'
                            : 'Periodic check-in every 30 days'}
                    </p>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[11px] text-zinc-600">
                      {isComplete
                        ? 'The LOA follow-up campaign has been completed successfully. No further actions are needed.'
                        : isStopped
                          ? 'This campaign has been stopped and will not send any further follow-up communications.'
                          : 'After all escalation steps are exhausted, the system continues with monthly follow-up emails until the LOA is received.'}
                    </p>
                    {campaign.next_action_date &&
                      !isPaused &&
                      !isComplete &&
                      !isStopped && (
                        <p className="mt-2 text-[11px] font-medium text-zinc-700">
                          Next email:{' '}
                          <span className="text-sky-600">
                            {formatShortDate(campaign.next_action_date)}
                          </span>
                        </p>
                      )}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}
        </div>

        {/* Scheduled Next Step - show when waiting for auto step */}
        {!isComplete &&
          !isPaused &&
          !isStopped &&
          !currentStep?.requires_manual_task &&
          campaign.next_action_date &&
          !isMonthlyCadence && (
            <div className="flex items-center justify-center gap-2 px-3 py-2 border-t border-zinc-100 bg-zinc-50/50">
              <Clock className="h-3.5 w-3.5 text-zinc-400" />
              <span className="text-[11px] font-medium text-zinc-600">
                {currentStep?.label.replace(/Day \d+:\s*/, '')} scheduled for{' '}
                <span className="font-semibold text-zinc-800">
                  {formatShortDate(campaign.next_action_date)}
                </span>
                <span className="text-zinc-500">
                  {' '}
                  ({formatRelativeTime(campaign.next_action_date)})
                </span>
              </span>
            </div>
          )}

        {/* Monthly Cadence scheduled info */}
        {isMonthlyCadence && campaign.next_action_date && (
          <div className="flex items-center justify-center gap-2 px-3 py-2 border-t border-zinc-100 bg-sky-50/50">
            <RefreshCw className="h-3.5 w-3.5 text-sky-500" />
            <span className="text-[11px] font-medium text-zinc-600">
              Monthly follow-up scheduled for{' '}
              <span className="font-semibold text-sky-700">
                {formatShortDate(campaign.next_action_date)}
              </span>
              <span className="text-zinc-500">
                {' '}
                ({formatRelativeTime(campaign.next_action_date)})
              </span>
            </span>
          </div>
        )}

        {/* Action Required Row - only show when user action is needed */}
        {!isComplete &&
          currentStep?.requires_manual_task &&
          !isPaused &&
          !isStopped && (
            <div className="flex flex-col border-t border-zinc-100">
              <div className="flex items-center justify-between px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-white">
                    <span className="text-[10px] font-bold">!</span>
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold text-zinc-700">
                      {currentStep?.label.replace(/Day \d+:\s*/, '') ||
                        'Action Required'}
                    </p>
                    {campaign.next_action_date && (
                      <p className="text-[10px] font-medium text-zinc-500">
                        {formatRelativeTime(campaign.next_action_date) ===
                        'overdue' ? (
                          <span className="text-amber-600">
                            Was scheduled for{' '}
                            {formatShortDate(campaign.next_action_date)}
                          </span>
                        ) : (
                          <>
                            Scheduled for{' '}
                            <span className="text-zinc-700">
                              {formatShortDate(campaign.next_action_date)}
                            </span>
                            <span className="text-zinc-400">
                              {' '}
                              · You&apos;ll be notified
                            </span>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <SmartCTA
                  currentStep={currentStep}
                  onAction={handleAction}
                  onMakeCall={handleMakeCall}
                  onOpenEmailPreview={handleOpenEmailPreview}
                  loading={loading}
                />
              </div>
            </div>
          )}

        {/* Paused/Stopped Row */}
        {!isComplete && (isPaused || isStopped) && (
          <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-t border-zinc-100">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-white',
                  isPaused ? 'bg-amber-500' : 'bg-zinc-500',
                )}
              >
                {isPaused ? (
                  <Pause className="h-2.5 w-2.5" />
                ) : (
                  <span className="text-[10px] font-bold">×</span>
                )}
              </span>
              <div>
                <p
                  className={cn(
                    'text-[11px] font-semibold',
                    isPaused ? 'text-amber-700' : 'text-zinc-600',
                  )}
                >
                  {isPaused ? 'Campaign Paused' : 'Campaign Stopped'}
                </p>
                <p className="text-[10px] text-zinc-500 mt-0.5">
                  {isPaused
                    ? 'Automated follow-ups are on hold. Resume to continue outreach.'
                    : 'This campaign has been stopped and will not send further follow-ups.'}
                </p>
              </div>
            </div>

            {isPaused && (
              <button
                type="button"
                onClick={() => handleAction('resume', {})}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                Resume
              </button>
            )}
          </div>
        )}

        {/* Complete State */}
        {isComplete && (
          <div className="flex items-center justify-center gap-1.5 px-3 py-2.5">
            <Check className="h-4 w-4 text-emerald-500" />
            <span className="text-xs font-semibold text-emerald-600">
              LOA Received
            </span>
          </div>
        )}

        {/* Deliverables Section */}
        {deliverables && deliverables.length > 0 && (
          <div className="px-3 py-2 border-t border-zinc-100">
            <div className="flex items-center gap-1.5 mb-1.5">
              <FileText className="h-3 w-3 text-zinc-400" />
              <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
                Deliverables
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <TooltipProvider delayDuration={200}>
                {deliverables.map((deliverable) => {
                  const approvedCount = deliverable.matches.filter(
                    (m) => m.status === DeliverableMatchStatus.APPROVED,
                  ).length;
                  const needsReviewCount = deliverable.matches.filter(
                    (m) => m.status === DeliverableMatchStatus.NEEDS_REVIEW,
                  ).length;
                  const hasConversation = deliverable.matches.some(
                    (m) => m.front_conversation_id,
                  );
                  // Find a transcript/audio artifact match for linking
                  const transcriptMatch = deliverable.matches.find(
                    (m) =>
                      m.artifact_id &&
                      (m.artifact_type === ArtifactType.TRANSCRIPT ||
                        m.artifact_type === ArtifactType.AUDIO_CONVERSATION),
                  );

                  return (
                    <Tooltip key={deliverable.id}>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium',
                            deliverable.status === DeliverableStatus.SATISFIED &&
                              'bg-emerald-50 text-emerald-700',
                            deliverable.status === DeliverableStatus.BYPASSED &&
                              'bg-zinc-100 text-zinc-500',
                            deliverable.status === DeliverableStatus.PENDING &&
                              'bg-amber-50 text-amber-700',
                          )}
                        >
                          {deliverable.status === DeliverableStatus.SATISFIED && (
                            <CheckCircle2 className="h-3 w-3" />
                          )}
                          {deliverable.status === DeliverableStatus.BYPASSED && (
                            <MinusCircle className="h-3 w-3" />
                          )}
                          {deliverable.status === DeliverableStatus.PENDING && (
                            <Clock className="h-3 w-3" />
                          )}
                          <span className="max-w-[100px] truncate">
                            {deliverable.name}
                          </span>
                          {hasConversation && (
                            <MessageSquare className="h-2.5 w-2.5 opacity-60" />
                          )}
                          {transcriptMatch && matterId && (
                            <InspectArtifactButton
                              artifactId={transcriptMatch.artifact_id!}
                              artifactType={transcriptMatch.artifact_type!}
                              matterId={matterId}
                              compact
                              icon={<Headphones className="h-2.5 w-2.5" />}
                            />
                          )}
                          {approvedCount > 0 && (
                            <span className="text-[10px] text-emerald-600">
                              ✓{approvedCount}
                            </span>
                          )}
                          {needsReviewCount > 0 && (
                            <span className="text-[10px] text-amber-600">
                              ?{needsReviewCount}
                            </span>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[200px]">
                        <p className="font-medium">{deliverable.name}</p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          {deliverable.matches.length} match
                          {deliverable.matches.length !== 1 ? 'es' : ''}
                          {approvedCount > 0 && ` · ${approvedCount} approved`}
                          {needsReviewCount > 0 &&
                            ` · ${needsReviewCount} needs review`}
                        </p>
                        {transcriptMatch && (
                          <p className="text-[11px] text-violet-500 mt-0.5">
                            Click headphones icon to view transcript
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>
        )}

        {/* Minimal Stats Footer */}
        <div className="flex items-center justify-center gap-3 px-3 py-1.5 bg-zinc-50/50 border-t border-zinc-100 text-[11px] font-medium text-zinc-600">
          <span>{campaign.total_contact_count} contacts</span>
          <span>·</span>
          <span>Last: {formatShortDate(campaign.last_contact_date)}</span>
        </div>
      </div>

      {/* Modals */}
      <ActivityModal
        open={activityModalOpen}
        onClose={() => setActivityModalOpen(false)}
        activities={activities}
        campaign={campaign}
      />
      <DebugModal
        open={debugModalOpen}
        onClose={() => setDebugModalOpen(false)}
        campaign={campaign}
        slaSteps={slaSteps}
      />
      <MakeCallModal
        isOpen={callModalOpen}
        onClose={() => setCallModalOpen(false)}
        initialContactLabel={carrierName || 'Carrier'}
        matterId={matterId}
        source="loa_chaser_progress"
      />
      <EmailPreviewModal
        open={emailPreviewModalOpen}
        onClose={() => setEmailPreviewModalOpen(false)}
        campaignId={campaign.id}
        stepId={emailPreviewStepId}
        onEmailSent={handleEmailSent}
      />
    </>
  );
};

export default LOAChaserProgress;
